import { useEffect, useState } from 'react';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApiClient } from '../../lib/apiClient';
import type { ManualTestDefinition } from '../../lib/manualTests';
import { useI18n } from '../../i18n/LanguageContext';

type LocationState = {
  message?: string;
};

const manualTestsItOverrides: Record<string, string> = {
  'Manual Tests': 'Test manuali',
  'Manage the manual test definitions that appear inside patient charts and AI-assisted workflows.':
    'Gestisci le definizioni dei test manuali che compaiono nelle cartelle paziente e nei flussi assistiti dall’IA.',
  'Add New Test': 'Aggiungi nuovo test',
  'Loading definitions...': 'Caricamento definizioni...',
  'No manual tests configured yet.': 'Nessun test manuale configurato.',
  'Create your first manual test to make it available in patient charts.':
    'Crea il primo test manuale per renderlo disponibile nelle cartelle paziente.',
};

function createManualTestsTranslator(language: string, tr: (value: string) => string) {
  if (language !== 'it') return tr;
  return (phrase: string) => {
    const normalized = phrase.trim();
    if (!normalized) return phrase;
    const globalTranslation = tr(normalized);
    if (globalTranslation !== normalized) return phrase.replace(normalized, globalTranslation);
    const local = manualTestsItOverrides[normalized];
    return local ? phrase.replace(normalized, local) : phrase;
  };
}

export default function SettingsManualTests() {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchJson, fetchApi } = useApiClient();
  const { language, tr } = useI18n();
  const tm = createManualTestsTranslator(language, tr);
  const [definitions, setDefinitions] = useState<ManualTestDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>((location.state as LocationState | null)?.message || null);
  const [deletingCode, setDeletingCode] = useState<string | null>(null);

  useEffect(() => {
    loadDefinitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!success) return;
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, navigate, success]);

  async function loadDefinitions() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJson<{ definitions: ManualTestDefinition[] }>('/manual-tests/definitions');
      setDefinitions(Array.isArray(data.definitions) ? data.definitions : []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load manual test definitions.');
    } finally {
      setLoading(false);
    }
  }

  async function removeDefinition(code: string) {
    if (!window.confirm('Delete this manual test definition?')) return;

    setDeletingCode(code);
    setError(null);
    setSuccess(null);
    try {
      const nextDefinitions = definitions.filter((definition) => definition.code !== code);
      const response = await fetchApi('/manual-tests/definitions', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ definitions: nextDefinitions }),
      });
      if (!response.ok) throw new Error(`Delete failed (${response.status})`);
      setDefinitions(nextDefinitions);
      setSuccess('Manual test definition deleted.');
    } catch (err: any) {
      setError(err?.message || 'Failed to delete definition.');
    } finally {
      setDeletingCode(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/settings')} className="rounded-full p-2 transition hover:bg-slate-100">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink">{tm('Manual Tests')}</h1>
            <p className="text-sm text-slate-500">
              {tm('Manage the manual test definitions that appear inside patient charts and AI-assisted workflows.')}
            </p>
          </div>
        </div>
        <button
          className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-white transition hover:bg-slate-800"
          onClick={() => navigate('/settings/manual-tests/new')}
        >
          <Plus size={16} />
          {tm('Add New Test')}
        </button>
      </div>

      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div> : null}
      {success ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{success}</div> : null}

      <div className="card overflow-hidden p-0">
        {loading ? (
          <div className="p-6 text-sm text-slate-500">{tm('Loading definitions...')}</div>
        ) : !definitions.length ? (
          <div className="flex min-h-[260px] flex-col items-center justify-center gap-4 p-6 text-center">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{tm('No manual tests configured yet.')}</h2>
              <p className="mt-1 text-sm text-slate-500">{tm('Create your first manual test to make it available in patient charts.')}</p>
            </div>
            <button
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
              onClick={() => navigate('/settings/manual-tests/new')}
            >
              <Plus size={16} />
              {tm('Add New Test')}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-6 py-4 font-medium">Name</th>
                  <th className="px-6 py-4 font-medium">Code</th>
                  <th className="px-6 py-4 font-medium">Category</th>
                  <th className="px-6 py-4 font-medium">Fields</th>
                  <th className="px-6 py-4 font-medium">Image Required</th>
                  <th className="px-6 py-4 font-medium">AI Prompt</th>
                  <th className="px-6 py-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {definitions.map((definition) => (
                  <tr key={definition.code} className="align-top">
                    <td className="px-6 py-4">
                      <div className="font-medium text-slate-900">{definition.name}</div>
                      <div className="mt-1 max-w-md text-sm text-slate-500">{definition.description?.trim() || 'No description provided.'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{definition.code}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{definition.category?.trim() || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{definition.fields.length}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{definition.requiresImage ? 'Yes' : 'No'}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{definition.aiPrompt?.trim() ? 'Configured' : 'Not set'}</td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50"
                          onClick={() => navigate(`/settings/manual-tests/${definition.code}`)}
                        >
                          <Pencil size={14} />
                          Edit
                        </button>
                        <button
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                          onClick={() => removeDefinition(definition.code)}
                          disabled={deletingCode === definition.code}
                        >
                          <Trash2 size={14} />
                          {deletingCode === definition.code ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
